import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import axios from 'axios';
import FileUploader from '../../components/FileUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    titleArabic: '',
    description: '',
    descriptionArabic: '',
    date: '',
    dateArabic: '',
    location: '',
    locationArabic: '',
    image: '',
    category: '',
    organizer: '',
    capacity: '',
    registrationLink: '',
    brochures: [],
    status: 'current',
    notes: '',
    notesArabic: '',
    completedDate: '',
    completedDateArabic: '',
    plannedDate: '',
    plannedDateArabic: ''
  });

  const [brochureForm, setBrochureForm] = useState({ title: '', url: '' });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchEvents(token);
  }, [navigate]);

  const fetchEvents = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('adminToken');

    try {
      if (editingEvent) {
        await axios.put(`${BACKEND_URL}/api/admin/events/${editingEvent.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents(token);
      alert(editingEvent ? 'Event updated successfully!' : 'Event created successfully!');
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error saving event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      titleArabic: event.titleArabic || '',
      description: event.description || '',
      descriptionArabic: event.descriptionArabic || '',
      date: event.date || '',
      dateArabic: event.dateArabic || '',
      location: event.location || '',
      locationArabic: event.locationArabic || '',
      image: event.image || '',
      category: event.category || '',
      organizer: event.organizer || '',
      capacity: event.capacity || '',
      registrationLink: event.registrationLink || '',
      brochures: event.brochures || [],
      status: event.status || 'current',
      notes: event.notes || '',
      notesArabic: event.notesArabic || '',
      completedDate: event.completedDate || '',
      completedDateArabic: event.completedDateArabic || '',
      plannedDate: event.plannedDate || '',
      plannedDateArabic: event.plannedDateArabic || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEvents(token);
      alert('Event deleted successfully!');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error deleting event. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', titleArabic: '', description: '', descriptionArabic: '',
      date: '', dateArabic: '', location: '', locationArabic: '',
      image: '', category: '', organizer: '', capacity: '',
      registrationLink: '', brochures: [], status: 'current', notes: '', notesArabic: '',
      completedDate: '', completedDateArabic: '', plannedDate: '', plannedDateArabic: ''
    });
    setBrochureForm({ title: '', url: '' });
  };

  const handleAddBrochure = () => {
    if (brochureForm.title && brochureForm.url) {
      setFormData({
        ...formData,
        brochures: [...formData.brochures, { title: brochureForm.title, url: brochureForm.url }]
      });
      setBrochureForm({ title: '', url: '' });
    }
  };

  const handleRemoveBrochure = (index) => {
    setFormData({
      ...formData,
      brochures: formData.brochures.filter((_, i) => i !== index)
    });
  };

  const handlePdfUpload = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/pdf`, formDataUpload);
      return response.data.url;
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Error uploading PDF');
      return null;
    }
  };

  const groupedEvents = {
    previous: events.filter(e => e.status === 'previous'),
    current: events.filter(e => e.status === 'current'),
    future: events.filter(e => e.status === 'future')
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="text-slate-600 hover:text-slate-800">
                <ArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-800">Event Management</h1>
            </div>
            <button
              onClick={() => { setShowForm(true); setEditingEvent(null); resetForm(); }}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
            >
              <Plus size={20} />
              Add New Event
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {showForm ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{editingEvent ? 'Edit Event' : 'Add New Event'}</h2>
              <button onClick={() => { setShowForm(false); setEditingEvent(null); }} className="text-slate-500 hover:text-slate-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Title (English) *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Title (Arabic) *</label>
                  <input type="text" name="titleArabic" value={formData.titleArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Description (English) *</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} required rows="3" className="w-full px-3 py-2 border rounded-lg"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Description (Arabic) *</label>
                  <textarea name="descriptionArabic" value={formData.descriptionArabic} onChange={handleInputChange} required rows="3" className="w-full px-3 py-2 border rounded-lg" dir="rtl"></textarea>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Date *</label>
                  <input type="text" name="date" value={formData.date} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" placeholder="March 15-17, 2026" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Date (Arabic) *</label>
                  <input type="text" name="dateArabic" value={formData.dateArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" placeholder="15-17 مارس 2026" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Location *</label>
                  <input type="text" name="location" value={formData.location} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Location (Arabic) *</label>
                  <input type="text" name="locationArabic" value={formData.locationArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Category *</label>
                  <input type="text" name="category" value={formData.category} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" placeholder="Conference" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Organizer</label>
                  <input type="text" name="organizer" value={formData.organizer} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Capacity</label>
                  <input type="text" name="capacity" value={formData.capacity} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" placeholder="500 attendees" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <FileUploader
                    type="image"
                    label="Event Image"
                    currentUrl={formData.image}
                    onUploadSuccess={(url) => setFormData({ ...formData, image: url })}
                  />
                  <input 
                    type="text" 
                    name="image" 
                    value={formData.image} 
                    onChange={handleInputChange} 
                    className="w-full px-3 py-2 border rounded-lg mt-2" 
                    placeholder="Or paste image URL here"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Registration Link</label>
                  <input type="url" name="registrationLink" value={formData.registrationLink} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" placeholder="https://..." />
                </div>
              </div>

              {/* PDF Brochures Section */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold mb-3">PDF Brochures</label>
                
                {/* Existing Brochures */}
                {formData.brochures.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {formData.brochures.map((brochure, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div>
                          <span className="font-medium text-sm">{brochure.title}</span>
                          <a href={brochure.url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 ml-3">View PDF</a>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveBrochure(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Brochure */}
                <div className="grid md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Brochure Title (e.g., Event Agenda)"
                    value={brochureForm.title}
                    onChange={(e) => setBrochureForm(prev => ({ ...prev, title: e.target.value }))}
                    className="px-3 py-2 border rounded-lg"
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const url = await handlePdfUpload(file);
                          if (url) {
                            setBrochureForm(prev => ({ ...prev, url }));
                          }
                        }
                      }}
                      className="px-3 py-2 border rounded-lg w-full text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddBrochure}
                    disabled={!brochureForm.title || !brochureForm.url}
                    className="bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    + Add Brochure
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Upload PDF brochures (max 10MB each)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Status *</label>
                <select name="status" value={formData.status} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg">
                  <option value="previous">Previous (Completed)</option>
                  <option value="current">Current (Ongoing)</option>
                  <option value="future">Future (Upcoming)</option>
                </select>
              </div>

              <div className="flex gap-4 pt-4">
                <button type="submit" disabled={loading} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg disabled:opacity-50">
                  <Save size={20} />
                  {loading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditingEvent(null); }} className="px-6 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold mb-4 text-purple-700">Current Events ({groupedEvents.current.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {groupedEvents.current.map(event => (
                  <div key={event.id} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
                    <h3 className="font-bold text-lg mb-2">{event.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{event.date} • {event.location}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(event)} className="flex items-center gap-1 text-orange-600 hover:text-orange-700">
                        <Edit size={16} /> Edit
                      </button>
                      <button onClick={() => handleDelete(event.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold mb-4 text-slate-700">Previous Events ({groupedEvents.previous.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {groupedEvents.previous.map(event => (
                  <div key={event.id} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-slate-400">
                    <h3 className="font-bold text-lg mb-2">{event.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{event.date}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(event)} className="flex items-center gap-1 text-orange-600 hover:text-orange-700">
                        <Edit size={16} /> Edit
                      </button>
                      <button onClick={() => handleDelete(event.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold mb-4 text-blue-700">Future Events ({groupedEvents.future.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {groupedEvents.future.map(event => (
                  <div key={event.id} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
                    <h3 className="font-bold text-lg mb-2">{event.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{event.date}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(event)} className="flex items-center gap-1 text-orange-600 hover:text-orange-700">
                        <Edit size={16} /> Edit
                      </button>
                      <button onClick={() => handleDelete(event.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManagement;